<script setup lang="ts">
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { Button, ButtonVariant, useFocusTrap } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { computed, onMounted, onUnmounted } from 'vue';
  import { useSidePanelStore } from '../../store/side-panel.store';
  import {
    closeSidePanelButtonClass,
    sidePanelBackgroundClass,
    sidePanelContainerClass,
  } from './SidePanel.css';

  const { t } = useLocale();
  const sidePanelStore = useSidePanelStore();

  const focusTrap = useFocusTrap(
    sidePanelStore.sidePanelState.config?.firstSelectionOnLastItem,
  );

  function keydownListener(event: KeyboardEvent) {
    if (
      event.key === 'Escape' &&
      sidePanelStore.isSidePanelOpen &&
      !sidePanelStore.sidePanelState.config?.blocking
    )
      sidePanelStore.closeSidePanel();
  }

  onMounted(() => {
    document.addEventListener('keydown', keydownListener);
  });

  onUnmounted(() => {
    document.removeEventListener('keydown', keydownListener);
  });

  const closeOnBackdrop = () => {
    if (!sidePanelStore.sidePanelState.config?.blocking)
      sidePanelStore.closeSidePanel();
  };

  const isVisible = computed<boolean>(() => {
    return !!sidePanelStore.sidePanelState.component;
  });
</script>

<template>
  <div>
    <Transition name="fade" appear>
      <!-- biome-ignore lint/a11y/noStaticElementInteractions: backdrop, not a control; a role would add a spurious tab stop -->
      <!-- biome-ignore lint/a11y/useKeyWithClickEvents: the keyboard equivalent is the document-level Escape listener above -->
      <div
        v-if="isVisible"
        :class="sidePanelBackgroundClass"
        @click.self="closeOnBackdrop"
      />
    </Transition>
    <Transition name="slide-in" appear>
      <div
        v-if="isVisible"
        :ref="focusTrap.trapRef"
        :class="sidePanelContainerClass"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
      >
        <Button
          :variant="ButtonVariant.Icon"
          is-ghost
          :class="closeSidePanelButtonClass"
          :icon-name="TUNDR_ICONS.CLOSE"
          :aria-label="t('common:actions.close')"
          @click="sidePanelStore.closeSidePanel"
        />
        <component
          :is="sidePanelStore.sidePanelState.component"
          v-bind="sidePanelStore.sidePanelState.props"
        />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
  .slide-in-enter-active {
    transition: 0.3s ease-out all;
    transition-delay: 0.3s;
  }

  .slide-in-leave-active {
    transition: 0.3s ease-in all;
  }

  .slide-in-enter-from,
  .slide-in-leave-to {
    transform: translateX(100%);
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .fade-enter-active {
    transition: 0.3s ease all;
  }

  .fade-leave-active {
    transition: 0.2s ease all;
    transition-delay: 0.3s;
  }
</style>
